import { useEffect, useState, useRef } from 'react';
import { isInIframe } from '@shared/utils/isInIframe';
import type { FeatureSet } from '@shared/core/featureSet/featureSet.types';
import { useHistory } from '@react-router';
import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { withWindow } from '@shared/utils/withWindow';
import type { StaticRouterProps } from '@react-router';

const isIframed = isInIframe();
const isSSR = withWindow(() => false, true);

/**
 * The hook will be responsible for redirecting to the enabled featureSet with the highest priority.
 * You can see the details of the operation in the following document:
 * @link https://withjoy.atlassian.net/wiki/spaces/LNCH/pages/2666561550/Standalone+Registry#CRAWL-(Cost-~1-sprint)
 *
 * During SSR, redirects are set on staticContext so the server sends a 302 response
 * instead of rendering the full page (which would flash before the client-side redirect).
 */
export const useGuestSiteRedirection = (featureSet: Maybe<FeatureSet>, staticContext?: StaticRouterProps['context']) => {
  const eventNameMatch = useEventNameMatch();
  const history = useHistory();
  const hasHandledSsrRedirectRef = useRef(false);

  const [waitingRedirection, setWaitingRedirection] = useState<boolean>(!isIframed);

  // SSR redirect: set staticContext.url so the server responds with a 302
  // instead of rendering the full guest site page. This prevents a flash of
  // content before the client-side redirect fires on hydration.
  if (isSSR && featureSet && !isIframed && staticContext && !hasHandledSsrRedirectRef.current) {
    if (!featureSet.website) {
      hasHandledSsrRedirectRef.current = true;
      if (featureSet.registry) {
        staticContext.url = `/${eventNameMatch.eventName}/registry`;
        staticContext.statusCode = 302;
      } else if (featureSet.rsvp) {
        staticContext.url = `/${eventNameMatch.eventName}/rsvp`;
        staticContext.statusCode = 302;
      } else {
        staticContext.url = '/find';
        staticContext.statusCode = 302;
      }
    }
  }

  useEffect(() => {
    if (featureSet && !isIframed) {
      if (featureSet.website) {
        // If we have the website featureSet enabled, we don't need to do any redirection.
      } else if (featureSet.registry) {
        history.replace(`/${eventNameMatch.eventName}/registry`);
      } else if (featureSet.rsvp) {
        withWindow(global => {
          // Full redirection to RSVP
          global.location.href = `/${eventNameMatch.eventName}/rsvp`;
        });
      } else {
        withWindow(global => {
          // On localhost, this redirection will fail since find-a-couple is being handled by marcom.
          global.location.href = '/find';
        });
      }
      setWaitingRedirection(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureSet]);

  return {
    waitingRedirection,
  };
};
